import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button, Checkbox } from 'antd';
import { CheckCircleTwoTone } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import FadeIn from '../FadeIn';
import ConfirmInformationIllustration from '../../resources/assets/illustrations/ConfirmInformationIllustration';
import PendingVerification from './status/PendingVerification';
import CompleteVerification from './status/CompleteVerification';
import useApiClient from '../../hooks/api/useApiClient';
import LoadingIndicator from '../LoadingIndicator';
import { OnboardingStep, PaymentGateway, PaymentGatewayVerificationStatus } from '../../services/exports/Constants';
import { BusinessProfileUpdateStrategy } from '../../providers/BusinessProfileProvider';
import OnboardingLinkButton, { OnboardingLinkButtonMode } from '../buttons/OnboardingLinkButton';
import { describeStripeRequirement, groupStripeRequirements, isDocumentRequirement } from './stripeRequirements';

// The steps that upload what Stripe asks for: Germany's document slots, and the old ID step elsewhere.
const DOCUMENT_STEPS = [OnboardingStep.Documents, OnboardingStep.PersonalDocs];

export default function InfoSubmissionStep({ paymentGateway, moveForward, ...rest }) {
  const { CurrentCompanyManager } = useApiClient();
  const { company } = useSelector((state) => state.currentCompany);

  const status = company?.[`${paymentGateway}_verification_status`];

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    mount();
  }, []);

  const mount = () => {
    setTimeout(
      () =>
        CurrentCompanyManager.get()
          .then((response) => {
            if (!response.success) {
              return null;
            }

            const freshStatus = response.data[`${paymentGateway}_verification_status`];
            const isDone =
              freshStatus === PaymentGatewayVerificationStatus.Pending ||
              freshStatus === PaymentGatewayVerificationStatus.Verified;

            return isDone && moveForward();
          })
          .finally(() => setMounted(true)),
      2000,
    );
  };

  if (status === PaymentGatewayVerificationStatus.Pending) {
    return <PendingVerification />;
  }

  if (status === PaymentGatewayVerificationStatus.Verified) {
    return <CompleteVerification />;
  }

  const Content = {
    [PaymentGateway.Stripe]: StripeContent,
    [PaymentGateway.Adyen]: AdyenContent,
  }[paymentGateway];

  return (
    <FadeIn>
      {!mounted && (
        <div className="my-md">
          <LoadingIndicator />
        </div>
      )}
      {mounted && Content && <Content moveForward={moveForward} {...rest} />}
    </FadeIn>
  );
}

function AdyenContent() {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Verification:InfoSubmissionStep' });

  return (
    <div className="flex flex-col lg:flex-row lg:items-center mx-auto">
      <div className="relative lg:w-5/12 text-center max-w-lg mx-auto lg:max-w-none lg:text-left">
        <h5>{t('title')}</h5>
        <p className="mt-md text-gray-default">{t('payment_gateways.adyen.description')}</p>
        <div className="flex gap-2 mt-md">
          <OnboardingLinkButton paymentGateway={PaymentGateway.Adyen} text={t('buttons.proceed')} type="primary" />
          <OnboardingLinkButton
            paymentGateway={PaymentGateway.Adyen}
            text={t('buttons.copy_link')}
            mode={OnboardingLinkButtonMode.Copy}
          />
        </div>
      </div>
      <div className="relative lg:mt-0 flex-1 flex flex-col justify-center items-center lg:items-end lg:p-md lg:self-end order-first lg:order-last">
        <ConfirmInformationIllustration />
      </div>
    </div>
  );
}

function StripeContent({ moveForward, moveBackwards, previousStep }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Verification:InfoSubmissionStep' });

  const { company } = useSelector((state) => state.currentCompany);
  const { serviceProvider } = useSelector((state) => state.currentServiceProvider);

  const { BusinessProfilesManager } = useApiClient();

  const [loading, setLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const onProceed = async () => {
    setLoading(true);
    const { success } = await BusinessProfilesManager.update({
      terms_accepted: termsAccepted,
      update_strategy: BusinessProfileUpdateStrategy.All,
    });
    setLoading(false);

    if (!success) {
      return toast.error(t('toasts.something_went_wrong'));
    }

    return moveForward();
  };

  const tosAccepted = company.business_profile.stripe_tos_acceptance;
  const currentlyDue = company.merchant.stripe_requirements?.currently_due ?? [];

  if (tosAccepted && currentlyDue.length > 0) {
    // Stripe's reason sits next to its fields (it used to hide behind a hover), and each hint shows
    // only for what Stripe asks: documents come from the document step before this one, the
    // owner's details from My info, the rest from the Stripe profile the button opens. Outside a
    // document step (Back may lead to another payment provider's step) the documents go there too.
    const groups = groupStripeRequirements(company.merchant.stripe_requirements);
    const asksForDocuments = currentlyDue.some(isDocumentRequirement);
    const asksForDetails = currentlyDue.some((field) => !isDocumentRequirement(field));
    const documentStep = DOCUMENT_STEPS.includes(previousStep?.id) ? previousStep : null;

    // Names a field the way the owner knows it; the date of birth's three parts are one line.
    const labelsOf = (fields) => [
      ...new Set(
        fields.map((field) => {
          const { subject, detail, fallback } = describeStripeRequirement(field);
          const name = detail ? t(`payment_gateways.stripe.requirements.details.${detail}`) : fallback;

          return subject
            ? t('payment_gateways.stripe.requirements.of_subject', {
                subject: t(`payment_gateways.stripe.requirements.subjects.${subject}`),
                detail: name,
              })
            : name;
        }),
      ),
    ];

    return (
      <>
        <p className="mb-xs">{t('payment_gateways.stripe.labels.stripe_requirements')}</p>
        <ul className="flex flex-col gap-2" data-testid="stripe-requirements">
          {groups.map(({ reason, fields }) => (
            <li key={reason ?? 'without-reason'}>
              {reason && <p className="mb-0 text-gray-default">{reason}</p>}
              {labelsOf(fields).map((label) => (
                <div key={label}>{label}</div>
              ))}
            </li>
          ))}
        </ul>
        {asksForDetails && (
          <p className="mt-md text-gray-default">{t('payment_gateways.stripe.labels.details_hint')}</p>
        )}
        {asksForDocuments && (
          <p className="mt-md text-gray-default">
            {documentStep
              ? t('payment_gateways.stripe.labels.requirements_hint', { step: documentStep.name })
              : t('payment_gateways.stripe.labels.requirements_hint_profile')}
          </p>
        )}
        <div className="flex gap-2 mt-sm">
          <OnboardingLinkButton paymentGateway={PaymentGateway.Stripe} type="primary" />
          {moveBackwards && (
            <Button onClick={moveBackwards} data-testid="info-submission-back">
              {t('buttons.back')}
            </Button>
          )}
        </div>
      </>
    );
  }

  if (tosAccepted) {
    return (
      <div className="flex flex-col lg:flex-row lg:items-center mx-auto">
        <div className="relative lg:w-5/12 text-center max-w-lg mx-auto lg:max-w-none lg:text-left">
          <h5 className="inline-flex items-center gap-2">
            <CheckCircleTwoTone twoToneColor="#52c41a" />
            {t('payment_gateways.stripe.labels.awaiting_verification')}
          </h5>
          <p className="mt-md text-gray-default">
            {t('payment_gateways.stripe.labels.awaiting_verification_description')}
          </p>
          <div className="flex gap-2 mt-md">
            <OnboardingLinkButton paymentGateway={PaymentGateway.Stripe} />
          </div>
        </div>
        <div className="relative lg:mt-0 flex-1 flex flex-col justify-center items-center lg:items-end lg:p-md lg:self-end order-first lg:order-last">
          <ConfirmInformationIllustration />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row lg:items-center mx-auto">
      <div className="relative lg:w-5/12 text-center max-w-lg mx-auto lg:max-w-none lg:text-left">
        <h5>{t('title')}</h5>
        <p className="mt-md text-gray-default">{t('payment_gateways.stripe.description')}</p>
        <Checkbox checked={termsAccepted} className="mt-md" onChange={(e) => setTermsAccepted(e.target.checked)}>
          <Trans
            t={t}
            components={{
              FoodAmigosTerms: (
                <a href={serviceProvider.participation_agreement_url} target="_blank" rel="noreferrer noopener" />
              ),
              StripeTerms: (
                <a href="https://stripe.com/legal/connect-account" target="_blank" rel="noreferrer noopener" />
              ),
            }}
          >
            payment_gateways.stripe.form.terms_acceptance.label
          </Trans>
        </Checkbox>
        <Button type="primary" className="mt-md" loading={loading} disabled={!termsAccepted} onClick={onProceed}>
          {t('buttons.proceed')}
        </Button>
      </div>
      <div className="relative lg:mt-0 flex-1 flex flex-col justify-center items-center lg:items-end lg:p-md lg:self-end order-first lg:order-last">
        <ConfirmInformationIllustration />
      </div>
    </div>
  );
}
